import { CheckIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type StepState = 'complete' | 'active' | 'inactive';

interface StepTrackerItemProps {
  label: string;
  index: number;
  state: StepState;
  showLabel: boolean;
  showConnector: boolean;
}

// A step you have reached carries the same fill whether you are on it or past it: the
// checkmark is what separates the two, so the colour is free to mark how far you have got.
const INDICATOR_REACHED = 'bg-primary text-primary-foreground';
const INDICATOR_UPCOMING = 'bg-muted text-muted-foreground';

export const StepTrackerItem = ({
  label,
  index,
  state,
  showLabel,
  showConnector,
}: StepTrackerItemProps) => {
  const { t } = useTranslation('common');

  return (
    // `aria-current` belongs on the element that stands for the step, not on the label
    // inside it: assistive tech handles ARIA state on a bare span inconsistently.
    <div
      role="listitem"
      aria-current={state === 'active' ? 'step' : undefined}
      className="flex items-center gap-2"
    >
      <span
        data-testid={`step-icon-${index + 1}`}
        data-state={state}
        className={`type-label-sm grid size-6 shrink-0 place-items-center rounded-sm ${
          state === 'inactive' ? INDICATOR_UPCOMING : INDICATOR_REACHED
        }`}
      >
        {state === 'complete' ? (
          <CheckIcon
            role="img"
            aria-label={t('components.stepTracker.completed')}
            className="size-3.5"
          />
        ) : (
          index + 1
        )}
      </span>
      {showLabel && (
        <span
          className={`type-label-md ${
            state === 'inactive' ? 'text-muted-foreground' : 'text-foreground'
          }`}
        >
          {label}
        </span>
      )}
      {showConnector && (
        <span data-testid="step-connector" aria-hidden="true" className="h-px w-3.5 bg-border" />
      )}
    </div>
  );
};
